import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Send } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/api";
import { scrollToSection } from "@/lib/lenis";
import shipIcon from "@/assets/chat_ship_icon.png";

const GREETING =
  "Hi, I am the Ferguson's Compliance Assistant. I can help you with customs filing requirements, freight quotes, document checklists, trade lane information, and navigating our services. How can I help you today?";

const SESSION_KEY = "fergusons_chat_session_id";
const MESSAGES_KEY = "fergusons_chat_messages";

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const loadMessages = () => {
  try {
    const raw = localStorage.getItem(MESSAGES_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore corrupt cache
  }
  return [{ role: "assistant", content: GREETING }];
};

const QUICK_QUESTIONS = [
  "What documents do I need for sea freight?",
  "How do I track my shipment?",
  "What's the CARICOM preferential tariff rule?",
  "How do I start a customs filing?",
];

export const ChatWidget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadMessages);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  if (location.pathname.startsWith("/admin")) return null;

  const triggerAction = (actionType, payload) => {
    const eventName = actionType === "OPEN_FILING_FORM" ? "fergusons:prefill-filing" : "fergusons:prefill-quote";
    const anchor = actionType === "OPEN_FILING_FORM" ? "#customs-filing" : "#quote";

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
        scrollToSection(anchor);
      }, 350);
    } else {
      window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
      scrollToSection(anchor);
    }
  };

  const sendText = async (text) => {
    if (!text || streaming) return;

    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`Chat request failed with status ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const evt = JSON.parse(line.slice(5).trim());
          if (evt.type === "token") {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = {
                role: "assistant",
                content: next[next.length - 1].content + evt.content,
              };
              return next;
            });
          } else if (evt.type === "action") {
            triggerAction(evt.action_type, evt.payload);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "I'm having trouble connecting right now. Please try again shortly or call us directly.",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    const text = input.trim();
    sendText(text);
  };

  return (
    <>
      <button
        data-testid="chat-bubble-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open compliance assistant chat"
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-[#0A140F] hover:bg-[#0F1D16] border border-gold/40 hover:border-gold shadow-lg shadow-black/30 flex items-center justify-center transition-all hover:scale-105"
      >
        {open ? (
          <X size={22} className="text-gold" />
        ) : (
          <img src={shipIcon} alt="Chat with the Compliance Assistant" className="h-7 w-7 object-contain" />
        )}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] rounded-2xl shadow-2xl border border-gold/20 bg-white flex flex-col overflow-hidden"
        >
          <div className="bg-[#0A140F] px-4 py-3.5 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <img src={shipIcon} alt="" className="h-6 w-6 object-contain" />
              <div>
                <p className="text-sm font-display font-semibold text-[#F4F7F5] leading-tight">
                  Ferguson's Compliance Assistant
                </p>
                <p className="text-[10px] text-[#A1B0A6]">Customs &amp; freight guidance</p>
              </div>
            </div>
            <button
              data-testid="chat-close-btn"
              onClick={() => setOpen(false)}
              className="text-[#A1B0A6] hover:text-gold"
            >
              <X size={18} />
            </button>
          </div>

          <div
            ref={listRef}
            data-testid="chat-messages-list"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-cream"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-gold text-[#0A140F] font-medium"
                      : "bg-white border border-ink/10 text-ink"
                  }`}
                >
                  {m.role === "assistant" ? (
                    m.content ? (
                      <div className="prose-chat">
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <span className="inline-flex gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-ink-faint animate-pulse" />
                        <span className="h-1.5 w-1.5 rounded-full bg-ink-faint animate-pulse [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-ink-faint animate-pulse [animation-delay:300ms]" />
                      </span>
                    )
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            ))}

            {messages.length === 1 && !streaming && (
              <div data-testid="chat-quick-questions" className="flex flex-wrap gap-2 pt-1">
                {QUICK_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => sendText(q)}
                    data-testid={`chat-quick-question-${q.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-+|-+$/g, "")}`}
                    className="text-xs text-left px-3 py-1.5 rounded-full border border-gold/30 text-ink-soft hover:text-ink hover:border-gold/60 hover:bg-gold/5 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={sendMessage}
            data-testid="chat-input-form"
            className="shrink-0 border-t border-ink/10 bg-white p-3 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about customs, quotes, documents…"
              data-testid="chat-input"
              disabled={streaming}
              className="flex-1 bg-cream border border-ink/15 rounded-full px-4 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-gold/50 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              className="h-9 w-9 shrink-0 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] flex items-center justify-center transition-colors"
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
